import { editorials, people, proAffiliations, rightsManagers } from '@/store/browse/category-repo';
import urlToState from '@/store/browse/url-to-state';
import getContext from '@/utilities/get-context';
import parseCategories, { getAssetType } from '@/utilities/parse-categories';
import { TEMPLATE_DIFFICULTY_LEVELS } from '@/utilities/consts';

const browseEventsData = ({
  $route,
  categorySlug,
  product = null,
  location = null,
  clickSource,
}) => {
  let searchTerm = null;
  let locationLocal = location;
  if ($route.params.query || $route.query.q) {
    if (!locationLocal) {
      if ($route.params.query || $route.query.search_header) {
        locationLocal = 'header';
      } else {
        locationLocal = 'marketplace';
      }
    }
    searchTerm = $route.params.query || $route.query.q;
  }

  let filterAndCategoryData = {};
  if (categorySlug) {
    const categories = parseCategories(getContext().__MA__.browse_category_filters.categories);
    let category = '';
    Object.entries(categories).forEach(([key, value]) => {
      const found = value.find(({ slug }) => slug === categorySlug);
      if (found) {
        category = key;
      }
    });
    const state = urlToState($route.fullPath, categorySlug);
    const { categoryFilters } = state;
    let resolution = '';
    const resolutionSlug = categoryFilters.resolution?.slug;
    let orientation = '';
    const orientationSlug = categoryFilters.orientation?.slug;
    if (categoryFilters.hasResolutions) {
      resolution = resolutionSlug === 'all-resolutions' ? null : resolutionSlug;
    }
    if (categoryFilters.hasResolutions) {
      orientation = orientationSlug === 'all-orientations' ? null : orientationSlug;
    }
    let version = '';
    const versionSlug = categoryFilters.version?.slug;
    if (categoryFilters.hasVersions) {
      version = versionSlug === 'all-versions' ? null : versionSlug;
    }
    let fps = '';
    const fpsSlug = categoryFilters.fps?.slug;
    if (categoryFilters.hasFps) {
      fps = fpsSlug === 'all-fps' ? null : fpsSlug;
    }

    let proAffiliation = '';
    if (categoryFilters.hasProAffiliation) {
      if (categoryFilters.proAffiliated === 0) {
        proAffiliation = null;
      } else {
        proAffiliation = proAffiliations.find(
          item => item.slug === categoryFilters.proAffiliated,
        ).slug;
        proAffiliation = proAffiliation === -1 ? 'exclude' : 'only';
      }
    }

    let usageRights = '';
    if (categoryFilters.hasEditorials) {
      if (categoryFilters.editorial.slug === 'all-editorials') {
        usageRights = null;
      } else {
        usageRights = editorials.find(item => item.slug === categoryFilters.editorial.slug).slug;
      }
    }

    let contentIdStatus = '';
    if (categoryFilters.hasRightsManagers) {
      if (categoryFilters.rightsManager === 0) {
        contentIdStatus = null;
      } else {
        contentIdStatus = rightsManagers.find(
          item => item.slug === categoryFilters.rightsManager,
        ).slug;
        contentIdStatus = contentIdStatus === -1 ? 'exclude' : 'only';
      }
    }

    let filters = $route.query.subcategories?.split(',') || [];

    if ($route.params.subCategory) {
      filters.unshift($route.params.subCategory);
    }

    let isFilterApplied = !!(
      $route.params.subCategory ||
      $route.query.specs ||
      $route.query.subcategories ||
      $route.query.date_added ||
      $route.query.template_difficulty_level
    );

    let graphicsFilter = {};
    if (categoryFilters.slug === 'graphics') {
      graphicsFilter = graphicsBrowseEventsData({
        categoryFilters,
        $route,
      });
      isFilterApplied ||= graphicsFilter.is_filter_applied;
      filters = [...filters, ...graphicsFilter.filters];
    }
    filterAndCategoryData = {
      category,
      sub_category: categorySlug,
      asset_type: getAssetType(category, categorySlug),
      resolution,
      orientation,
      version,
      date_added: state.dateAdded === 'any-time' ? null : state.dateAdded,
      free_assets_filter: !!state.isFree,
      pro_affiliation: proAffiliation,
      usage_rights: usageRights,
      content_ID_filter: contentIdStatus,
      FPSs: fps,
      sort_by: state.sortBy,
      duration: categoryFilters.durations,
      bpm: categoryFilters.bpms,
      ...graphicsFilter,
      is_filter_applied: isFilterApplied,
      filters,
    };
  }

  let productRelatedData = {};
  if (product) {
    productRelatedData = {
      listing_position: product.listingPosition,
      title: product.title || product.name,
      is_new: product.isNew,
      is_free: product.isFree,
      asset_id: product.id,
      contributor: product.contributor || product.author,
      complexity_level_filter:
        product.template?.difficulty_level === TEMPLATE_DIFFICULTY_LEVELS.SIMPLE
          ? 'easy'
          : product.template?.difficulty_level,
    };
  }

  return {
    plan: getContext().__MA__.user.plan_name,
    click_source: clickSource,
    location: locationLocal,
    search_term: searchTerm,
    ...productRelatedData,
    ...filterAndCategoryData,
  };
};

const graphicsBrowseEventsData = ({ categoryFilters, $route }) => {
  let peopleFilter = '';
  if (categoryFilters.people.slug === 0) {
    peopleFilter = null;
  } else {
    peopleFilter = people.find(item => item.slug === categoryFilters.people.slug).slug;
    peopleFilter = peopleFilter === -1 ? 'exclude' : 'only';
  }

  const filters = [];
  if ($route.query.filetypes?.split(',')?.includes('vector')) {
    filters.push('vector');
  }

  let fileTypes = [];
  if ($route.query.filetypes) {
    fileTypes.push(...$route.query.filetypes?.split(',')?.filter(item => item !== 'vector'));
  } else {
    fileTypes = null;
  }

  let applicationsSupported = [];
  if ($route.query.applicationsSupported) {
    applicationsSupported.push(...$route.query.applicationsSupported?.split(','));
  } else {
    applicationsSupported = null;
  }

  let themes = [];
  if ($route.query.themes) {
    themes.push(...$route.query.themes?.split(','));
  } else {
    themes = null;
  }

  let artstyles = [];
  if ($route.query.artstyles) {
    artstyles.push(...$route.query.artstyles?.split(','));
  } else {
    artstyles = null;
  }

  let propertiesFilter = [];
  if ($route.query.isolated === 'true') {
    propertiesFilter.push('isolated-assets');
  } else {
    propertiesFilter = null;
  }

  const subCategoryFilters = [];
  if ($route.query.subcategories) {
    subCategoryFilters.push(...$route.query.subcategories?.split(','));
  }

  const orientationSlug = categoryFilters.orientation?.slug;
  const orientation = orientationSlug === 'all-orientations' ? null : orientationSlug;

  return {
    people_filter: peopleFilter,
    file_type_filter: fileTypes,
    applications_supported_filter: applicationsSupported,
    themes_filter: themes,
    art_style_filters: artstyles,
    properties_filter: propertiesFilter,
    category_filters: subCategoryFilters,
    filters,
    orientation,
    is_filter_applied:
      !!peopleFilter ||
      fileTypes?.length > 0 ||
      applicationsSupported?.length > 0 ||
      themes?.length > 0 ||
      artstyles?.length > 0 ||
      propertiesFilter?.length > 0 ||
      subCategoryFilters.length > 0 ||
      !!orientation ||
      filters.length > 0,
  };
};

export default browseEventsData;
